import React from "react";
import {
    Badge,
    Box,
    Flex,
    Grid,
    GridItem,
    Link as ChakraLink,
    Icon,
    Spinner,
    Text,
    VStack,
    Separator
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { ArrowRightIcon } from "@digital-services-gard-as/anchor-icons";
import { Link as ReactRouterLink } from "react-router-dom";

import { ClaimsHandler } from "./ClaimsHandler";

import { useQuery } from "@tanstack/react-query";
import { Alert, BodyShort } from "@digital-services-gard-as/anchor-react";
import { StatusBadge } from "@/utils/invoiceUtils";
import { getClaimsInfoByBranchQueryOptions } from "@/api/queryOptions/claimsApiQueryOptions";
import { useSlowRequestToast } from "@/hooks/useSlowRequestToast";

type ClaimsDetailsProps = {
    claimsId: string;
};

const ClaimsDetails = ({ claimsId }: ClaimsDetailsProps) => {
    const { t } = useTranslation(["claimsInvoicesOverview", "errorMessages"]);
    const actOnBehalfOf = globalThis.sessionStorage.getItem("actOnBehalfOf");
    let selectedBranchCompany = actOnBehalfOf ? JSON.parse(actOnBehalfOf) : null;

    if (selectedBranchCompany === null) {
        selectedBranchCompany = "";
    }
    const {
        data: claimsInfo,
        isError: isClaimsInfoError,
        isFetching: isClaimsInfoFetching
    } = useQuery(
        getClaimsInfoByBranchQueryOptions({
            claimsId,
            crmId: selectedBranchCompany ? selectedBranchCompany?.crmId.toString() : ""
        })
    );

    useSlowRequestToast({
        isLoading: isClaimsInfoFetching
    });

    if (isClaimsInfoError) {
        return (
            <Flex backgroundColor="#FAFBFB" id="claimsDetailsError">
                <Alert id="claimsDetailsNoDataAlert" variant="danger" data-testid="claimsDetailsNoDataAlert">
                    <BodyShort>{t("errorMessages:claimsInvoicesOverview.cannotRetrieveSubclaim")}</BodyShort>
                </Alert>
            </Flex>
        );
    }

    if (isClaimsInfoFetching) {
        return (
            <Flex justifyContent="center" m="3rem 0">
                <VStack colorPalette="teal">
                    <Spinner />
                    <Text>{t("claimsInvoicesOverview:claimsDetails.subclaimsFetching")}</Text>
                </VStack>
            </Flex>
        );
    }

    const getInvoiceNumber = (invoiceNumber: string | undefined) =>
        `${t("claimsInvoicesOverview:claimsDetails.invoice") ?? ""} ${invoiceNumber ?? ""} `;

    return (
        <Box backgroundColor="anchor.brown.100" id="claimsDetails">
            <Grid templateColumns="repeat(5, 1fr)" rowGap="2rem" columnGap="5rem" p="2rem 3rem" maxW="100%">
                {claimsInfo?.subClaims.map((subClaim, subClaimIndex) => (
                    <React.Fragment key={subClaim.gardSubClaimRef}>
                        <GridItem maxW="25rem">
                            <VStack gap="0.5rem" align="baseline">
                                <Text data-testid={"subClaimId" + subClaimIndex}>
                                    {t("claimsInvoicesOverview:claimsDetails.subClaim")} {subClaim.gardSubClaimRef}
                                </Text>
                                <Badge bg="#DACEE8">{subClaim.claimType}</Badge>
                            </VStack>
                        </GridItem>
                        <GridItem colSpan={2}>
                            {(subClaim.invoices?.length ?? 0) >= 1 ? (
                                subClaim.invoices?.map((invoice, invoiceIndex) => {
                                    const invoiceCount = subClaim.invoices?.length ?? 0;
                                    return (
                                        <VStack key={invoice.id} align="baseline" gap="1rem">
                                            {invoiceIndex === 0 && (
                                                <Text whiteSpace="pre-wrap" overflowWrap="break-word" asChild>
                                                    <b>{subClaim.description ?? ""}</b>
                                                </Text>
                                            )}
                                            <ChakraLink color="#3379B9" asChild>
                                                <ReactRouterLink to={"/invoice/" + invoice.id}>
                                                    <Flex alignItems="center" gap="0.2rem">
                                                        <Text>{getInvoiceNumber(invoice.invoiceNumber)}</Text>
                                                        <Icon asChild>
                                                            <ArrowRightIcon />
                                                        </Icon>
                                                    </Flex>
                                                </ReactRouterLink>
                                            </ChakraLink>
                                            <StatusBadge
                                                id={"statusBadge" + subClaimIndex}
                                                invoiceStatus={invoice.status}
                                            />
                                            {invoiceCount > 1 && invoiceIndex !== invoiceCount - 1 && <Separator />}
                                        </VStack>
                                    );
                                })
                            ) : (
                                <VStack align="baseline">
                                    <Text whiteSpace="pre-wrap" overflowWrap="break-word" asChild>
                                        <b>{subClaim.description ?? ""}</b>
                                    </Text>
                                    <Text>{t("claimsInvoicesOverview:claimsDetails.noInvoices")}</Text>
                                </VStack>
                            )}
                        </GridItem>
                        <GridItem>
                            <ClaimsHandler
                                claimHandlerName={subClaim.claimHandlerName}
                                claimHandlerEmail={subClaim.claimHandlerEmail}
                            />
                        </GridItem>
                        {claimsInfo?.subClaims.length > 1 && subClaimIndex !== claimsInfo?.subClaims.length - 1 && (
                            <GridItem rowSpan={1} colSpan={5}>
                                <Separator />
                            </GridItem>
                        )}
                    </React.Fragment>
                ))}
                {claimsInfo?.invoicesWithoutSubclaim && claimsInfo.invoicesWithoutSubclaim.length > 0 && (
                    <>
                        <GridItem rowSpan={1} colSpan={5}>
                            <Separator />
                        </GridItem>
                        <GridItem maxW="25rem">
                            <Text fontSize="lg">
                                {t("claimsInvoicesOverview:claimsDetails.invoicesWithoutSubclaim")}
                            </Text>
                        </GridItem>
                        <GridItem colSpan={2}>
                            {claimsInfo.invoicesWithoutSubclaim.map((invoice, index) => {
                                return (
                                    <VStack key={invoice.id} align="baseline" gap="1rem">
                                        <ChakraLink color="#3379B9" asChild>
                                            <ReactRouterLink to={"/invoice/" + invoice.id}>
                                                <Flex alignItems="center" gap="0.2rem">
                                                    <Text>{getInvoiceNumber(invoice.invoiceNumber)}</Text>
                                                    <Icon asChild>
                                                        <ArrowRightIcon />
                                                    </Icon>
                                                </Flex>
                                            </ReactRouterLink>
                                        </ChakraLink>
                                        <StatusBadge
                                            id={"statusBadgeWithoutSubclaim" + index}
                                            invoiceStatus={invoice.status}
                                        />
                                    </VStack>
                                );
                            })}
                        </GridItem>
                    </>
                )}
            </Grid>
        </Box>
    );
};

export default React.memo(ClaimsDetails);
